import axios, { AxiosError } from "axios";
import API_ORIGIN from "../config";

/**
 * Axios instance for Laravel Sanctum SPA auth: session cookie + CSRF (XSRF-TOKEN).
 * Do not send Bearer tokens from localStorage.
 */
// throttle flag to avoid scheduling many redirects at once
let _redirectingToLogin = false;

const handleAuthError = async (err: AxiosError) => {
  const status = err.response?.status;
  if (status === 401 || status === 419) {
    const url = String(err.config?.url || "");
    if (url.includes("/auth/me") || url.includes("/auth/login")) {
      return Promise.reject(err);
    }
    const { default: router } = await import("../router");
    // Avoid repeated redirects when many requests fail at once
    // Use a short async break and a guard flag so we don't trigger
    // nested router navigations that lead to "too much recursion".
    if (router.currentRoute.value.path === "/") {
      return Promise.reject(err);
    }
    const { useAuthStore } = await import("../store/auth");
    useAuthStore().clearSession();

    // Defer actual navigation to break call stack and avoid recursion
    if (!_redirectingToLogin) {
      _redirectingToLogin = true;
      setTimeout(() => {
        router.replace("/").catch(() => undefined);
        _redirectingToLogin = false;
      }, 0);
    }
  }

  return Promise.reject(err);
};

const http = axios.create({
  baseURL: API_ORIGIN || undefined,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

http.defaults.xsrfCookieName = "XSRF-TOKEN";
http.defaults.xsrfHeaderName = "X-XSRF-TOKEN";

http.interceptors.response.use(
  (res) => res,
  handleAuthError
);

export async function fetchSanctumCsrfCookie(): Promise<void> {
  await http.get("/sanctum/csrf-cookie");
}

/**
 * Separate axios instance for file uploads (multipart/form-data).
 * Does NOT set Content-Type so axios can auto-set the boundary for FormData.
 */
export const httpMultipart = axios.create({
  baseURL: API_ORIGIN || undefined,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

httpMultipart.defaults.xsrfCookieName = "XSRF-TOKEN";
httpMultipart.defaults.xsrfHeaderName = "X-XSRF-TOKEN";

httpMultipart.interceptors.response.use(
  (res) => res,
  handleAuthError
);

export default http;
